import { useQuery } from '@tanstack/react-query';

import { useCurrentBusiness } from '~hooks/selectors/useCurrentBusiness';

import httpClient from '~api/httpClient';
import type { ApiError } from '~types/api';

// eslint-disable-next-line no-magic-numbers
const STALE_TIME = 5 * 60 * 1000;

interface VIPCustomer {
	name: string;
	email: string;
	visit_count?: number;
	total_spent?: number;
	customer_since?: string;
	days_as_customer?: number;
}

interface MostPopularDay {
	day_name?: string;
	day_of_week?: string;
	order_count?: string;
	usage_count?: string;
}

interface HourlyInboundMessage {
	count: number;
	hour: number;
}

interface MessengerMetrics {
	totalSentMessages: number;
	totalSentMessagesByAssist: number;
	hourlyInboundMessages: HourlyInboundMessage[];
}

interface MessengerResponse {
	metrics: MessengerMetrics;
	isMessagingEnabled: boolean;
}

interface AccelerateMetrics {
	totalCampaigns: number;
	totalReviews: number;
	totalFiveStarReviews: number;
	totalEmailsSent: number;
	totalEmailsOpened: number;
	totalLinksClicked: number;
	startDate: string;
}

interface AccelerateResponse {
	accelerateIntegrationEnabled: boolean;
	metrics: AccelerateMetrics;
}

type Folded2025Summary = {
	revenue: {
		total_revenue: number;
		yoy_growth_percentage: number;
		best_month: {
			month: string;
			revenue: number;
		};
		peak_hour: {
			hour: number;
			revenue: number;
			transaction_count: number;
			timezone_note?: string;
		};
		longest_growth_streak: {
			weeks: number;
			start_date: string;
			end_date: string;
			starting_revenue: number;
			ending_revenue: number;
			growth_percentage: number;
		};
	};

	customers: {
		total_new_customers: number;
		total_returning_customers: number;
		percent_new: number;
		percent_returning: number;
		total_customer_lifetime_value: number;
		most_popular_days: Array<{
			day_of_week: string;
			order_count: number;
		}>;
		vip_customers: {
			most_loyal: {
				name: string;
				email: string;
				visit_count: number;
			};
			highest_spender: {
				name: string;
				email: string;
				total_spent: number;
			};
			day_one: {
				name: string;
				email: string;
				customer_since: string;
				days_as_customer: number;
			};
		};
	};

	team: {
		mvp_by_pounds: {
			name: string;
			pounds_processed: number;
		};
		mvp_by_deliveries: {
			name: string;
			deliveries_completed: number;
		};
		mvp_by_hours: {
			name: string;
			hours_clocked: number;
		};
	};

	machines?: {
		total_pounds_processed: number;
		total_turns: number;
		washer_turns: number;
		dryer_turns: number;
		most_popular_days: Array<{
			day_of_week: string;
			turn_count: number;
		}>;
		fan_favorite: {
			machine_name: string;
			turn_count: number;
		};
		star_players: {
			machine_group_name: string;
			turn_count: number;
		};
		quarters_saved: {
			total_quarters: number;
			pounds_weight: number;
		};
	};

	pickup_delivery: {
		total_orders_delivered: number;
		fan_favorite_service: {
			service_name: string;
			order_count: number;
		};
		hot_wheels: {
			customer_name: string;
			delivery_time_minutes: number;
		};
		road_trip: {
			customer_name: string;
			delivery_time_minutes: number;
		};
	};
};

interface Folded2025AnnualSummaryResponse {
	status: string;
	year: number;
	summary: Folded2025Summary;
	generated_at: string;
}

interface AssistResponse {
	businessId: number;
	totalCalls: number;
	totalDurationMilliseconds: number;
	createdAt: number;
}

interface Folded2025FeatureData {
	messenger: MessengerResponse;
	accelerate: AccelerateResponse;
	assist: AssistResponse;
}

interface Folded2025Response {
	success: boolean;
	data: {
		data: Folded2025AnnualSummaryResponse;
	};
}

const fetchFolded2025AnnualSummary = async (
	businessId: number,
): Promise<Folded2025AnnualSummaryResponse> => {
	const response: Folded2025Response = await httpClient.get(
		`/common/barry/business/${businessId}/annual-summary?year=2025`,
	);
	return response.data.data;
};

const fetchFolded2025AccelerateData = async (
	businessId: number,
): Promise<AccelerateResponse> => {
	const response = await httpClient.get<AccelerateResponse>(
		`/common/business/${businessId}/metrics/accelerate`,
	);
	return response.data;
};

const fetchFolded2025MessengerData = async (
	businessId: number,
): Promise<MessengerResponse> => {
	const response = await httpClient.get<MessengerResponse>(
		`/common/business/${businessId}/metrics/messenger`,
	);
	return response.data;
};

const fetchFolded2025AssistCalls = async (
	businessId: number,
): Promise<AssistResponse> => {
	const response = await httpClient.get<AssistResponse>(
		`/common/business/${businessId}/metrics/assist-calls`,
	);
	return response.data;
};

export const useFolded2025Data = (
	enabled = true,
): ReturnType<typeof useQuery<unknown, ApiError, Folded2025AnnualSummaryResponse>> => {
	const business = useCurrentBusiness();
	const businessId = business?.id || 0;

	return useQuery<unknown, ApiError, Folded2025AnnualSummaryResponse>({
		queryKey: ['folded-2025'],
		queryFn: async () => {
			return fetchFolded2025AnnualSummary(businessId);
		},
		enabled: enabled && businessId > 0,
		staleTime: STALE_TIME,
		retry: 3,
	});
};

export const useFolded2025FeatureData = (): ReturnType<
	typeof useQuery<unknown, ApiError, Folded2025FeatureData>
> => {
	const business = useCurrentBusiness();
	const businessId = business?.id || 0;

	return useQuery<unknown, ApiError, Folded2025FeatureData>({
		queryKey: ['folded-feature-data-2025'],
		queryFn: async () => {
			const response = await Promise.all([
				fetchFolded2025MessengerData(businessId),
				fetchFolded2025AccelerateData(businessId),
				fetchFolded2025AssistCalls(businessId),
			]);
			return {
				messenger: response[0],
				accelerate: response[1],
				assist: response[2],
			};
		},
		enabled: false,
		staleTime: STALE_TIME,
		retry: 3,
	});
};
